import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import {
    SettingType, CodeBlock, BlockType, ValidationMessage, ValidationMessageType,
    SettingState,
    SettingOption,
} from '@appblocks/codegeneration/types';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';

export default class CustomFeatureCodeBlock extends BaseFeature {
    constructor() {
        super();
        this.name = 'Custom Feature Code Block';
        this.id = 'custom_feature_code_block';
        this.singleInstance = false;
        this.settings = this.settings.concat([
            {
                id: 'GENERAL',
                name: 'General',
                settingType: SettingType.GROUP,
                required: true,
                default: '',
            },
            {
                id: 'ADVANCED',
                name: 'Advanced',
                settingType: SettingType.GROUP,
                required: true,
                default: '',
            },
            {
                id: 'TYPE',
                name: 'Code Type',
                settingType: SettingType.OPTION,
                group: 'GENERAL',
                required: true,
                default: 'code',
                options: [
                    {
                        name: 'Code',
                        value: 'code',
                    },
                    {
                        name: 'File',
                        value: 'file',
                    },
                ],
            },
            {
                id: 'RUNTIME',
                name: 'Runtime',
                settingType: SettingType.OPTION,
                group: 'GENERAL',
                required: true,
                default: 'tios',
                options: [],
            },
            {
                id: 'NAME',
                name: 'File Name',
                settingType: SettingType.STRING,
                group: 'GENERAL',
                required: true,
                default: '',
            },
            {
                id: 'CODE_ID',
                name: 'Code ID',
                group: 'GENERAL',
                settingType: SettingType.STRINGVARIABLE,
                helpText: `
This is the unique identifier for the code block.

Only alphanumeric characters and underscores are allowed. 

Only one instance of a code block with the same ID can exist in the generated code.`,
                required: true,
                default: '',
            },
            {
                id: 'LOCATION',
                name: 'Code Location',
                settingType: SettingType.STRINGVARIABLE,
                group: 'GENERAL',
                required: true,
                helpText: `
This is where the code block will be placed in the generated code. 

The available locations depend on the runtime selected.

Context variables:
- \`{{feature}}\` - The feature object
- \`{{project}}\` - The project object
- \`{{parent}}\` - The parent feature object
- \`{{block.output}}\` - The output of the block

You can also look for the available locations by looking at the generated code for places marked:

\`\`\`javascript
${PlatformUtils.getRuntimeCodeLocationStart(this.project)}LOCATION_NAME${PlatformUtils.getRuntimeCodeLocationEnd(this.project)}
\`\`\`
                `,
                default: 'boot',
                options: [],
            },
            {
                id: 'CODE',
                name: 'Code',
                group: 'GENERAL',
                settingType: SettingType.CODE,
                helpText: `
This is the code that will be generated.

You can use the following variables:

\`{{feature}}\` - The feature object

\`{{project}}\` - The project object

Settings of the current feature can also be accessed from the autocomplete selection when you type in the setting id.
                `,
                required: true,
                default: '',
            },
            {
                id: 'CONDITION',
                name: 'Condition',
                group: 'ADVANCED',
                settingType: SettingType.STRING,
                required: true,
                default: '',
            },
        ]);
    }

    getState(settingId: string): SettingState {
        const TYPE = this.getSettingValue('TYPE');
        if (TYPE === 'code') {
            switch (settingId) {
                case 'NAME':
                    return SettingState.HIDDEN;
                default:
                    break;
            }
        }
        if (TYPE === 'file') {
            switch (settingId) {
                case 'LOCATION':
                    return SettingState.HIDDEN;
                default:
                    break;
            }
        }
        return SettingState.ENABLED;
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        if (settingId === 'LOCATION') {
            const feature = this.project.getParent(this);
            const TYPE = feature.getSettingValue('TYPE');
            let locations: any[] = [];
            const RUNTIME = this.getSettingValue('RUNTIME');
            switch (RUNTIME) {
                case 'tios':
                    locations = [
                        {
                            name: 'Boot',
                            value: 'boot',
                        },
                        {
                            name: 'Boot Complete',
                            value: 'boot_complete',
                        },
                        {
                            name: 'System Tick',
                            value: 'on_sys_timer',
                        },
                        {
                            name: 'Network Connected',
                            value: 'services_start',
                        },
                        {
                            name: 'Global Declarations',
                            value: 'GLOBAL_DECLARATIONS',
                        },
                        {
                            name: 'Global Variable Definitions',
                            value: 'GLOBAL_DIMS',
                        },
                        {
                            name: 'Function Definitions',
                            value: 'DEVICE_TBS',
                        },
                    ];
                    break;
                case 'zephyr':
                    locations = [
                        {
                            name: 'Boot',
                            value: 'boot',
                        },
                        {
                            name: 'Boot Complete',
                            value: 'boot_complete',
                        },
                        {
                            name: 'Global Includes',
                            value: 'GLOBAL_INCLUDES',
                        },
                        {
                            name: 'Global Variable Definitions',
                            value: 'VARIABLE_DECLARATIONS',
                        },
                        {
                            name: 'Function Definitions',
                            value: 'FUNCTION_DEFINITIONS',
                        },
                    ];
                    break;
                default:

                    break;
            }
            if (TYPE === 'block') {
                locations.push({
                    name: 'Block Triggered',
                    value: 'block_trigger',
                });
            }
            return locations;
        }
        if (settingId === 'RUNTIME') {
            const parent = this.project.getParent(this);
            const runtimes = [
                {
                    name: 'TiOS',
                    value: 'tios',
                },
                {
                    name: 'Micropython',
                    value: 'micropython',
                },
                {
                    name: 'Zephyr',
                    value: 'zephyr',
                },
            ];
            const parentRuntimes = parent.getSettingValue('RUNTIMES');
            const options = [];
            for (let i = 0; i < runtimes.length; i++) {
                if (parentRuntimes.includes(runtimes[i].value)) {
                    options.push(runtimes[i]);
                }
            }
            return options;
        }
        return super.getSettingOptions(settingId);
    }

    getCodeEditorContext() {
        let language = 'tibbobasic';
        const options: any[] = [];
        const parent = this.project.getParent(this);
        if (!parent) {
            return {
                language,
                options,
            };
        }
        const RUNTIME = this.project.runtime;
        switch (RUNTIME) {
            case 'tios':
                language = 'tibbobasic';
                break;
            case 'zephyr':
                language = 'c';
                break;
            default:
                break;
        }

        options.push({
            name: 'feature',
            value: 'feature',
            label: 'feature',
            category: 'Context',
        });

        options.push({
            name: 'feature.id',
            value: 'feature.id',
            label: 'feature.id',
            category: 'Context',
        });

        options.push({
            name: 'feature.settings.ID',
            value: 'feature.settings.ID',
            label: 'feature.settings.ID',
            category: 'Context',
        });

        const SETTINGS = parent.getSettingValue('SETTINGS');
        for (let i = 0; i < SETTINGS.length; i++) {
            const setting = SETTINGS[i];
            const SETTING_ID = setting.getSettingValue('SETTING_ID');
            options.push({
                name: `${SETTING_ID}`,
                value: `{{$${SETTING_ID}}}`,
                label: `${SETTING_ID}`,
                dataType: setting.getSettingValue('DATA_TYPE'),
                category: 'Settings',
            });

            options.push({
                name: `feature.settings.${SETTING_ID}`,
                value: `{{$feature.settings.${SETTING_ID}}}`,
                label: `feature.settings.${SETTING_ID}`,
                category: 'Context',
            });
        }

        options.push({
            name: 'parent',
            value: 'parent',
            label: 'parent',
            category: 'Context',
        });

        options.push({
            name: 'parent.id',
            value: 'parent.id',
            label: 'parent.id',
            category: 'Context',
        });

        options.push({
            name: 'parent.settings.ID',
            value: 'parent.settings.ID',
            label: 'parent.settings.ID',
            category: 'Context',
        });

        options.push({
            name: 'project',
            value: 'project',
            label: 'project',
            category: 'Context',
        });

        options.push({
            name: 'project.device',
            value: 'project.device',
            label: 'project.device',
            category: 'Context',
        });

        options.push({
            name: 'block.output',
            value: 'block.output',
            label: 'block.output',
            category: 'Context',
        });

        return {
            language,
            options,
        };
    }

    getCodeBlocks() {
        const blocks: Array<CodeBlock> = [];

        return blocks;
    }
}
